<script setup>
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { NCard, NInput, NButton, NAlert, NSpace, NSelect, NText, NForm, NFormItem, NRadioGroup, NRadio } from 'naive-ui'
import { register, isLoading, authError } from '@/services/auth'
import { LANGUAGES } from '@/services/language'
import OAuthButton from '@/components/OAuthButton.vue'
import { dt } from '@/utils/i18n'

const router = useRouter()
const route = useRoute()

const email = ref('')
const name = ref('')
const role = ref(route.query.role || 'patient')
const language = ref('english')
const submitStatus = ref(null)
const successMessage = ref('')

const returnUrl = computed(() => route.query.returnUrl || '/')

const handleRegister = async () => {
  if (!email.value || !name.value) {
    return
  }

  try {
    submitStatus.value = 'submitting'
    const result = await register(email.value, name.value, role.value, language.value)
    submitStatus.value = 'success'
    successMessage.value = result.message

    // Store email for verification
    localStorage.setItem('pending_verification_email', email.value)
    localStorage.setItem('pending_user_id', result.user_id)
    localStorage.setItem('pending_validation_type', result.verification_type)
    localStorage.setItem('preferredLanguage', language.value)
    // Redirect to verification page
    router.push({
      name: 'verify',
      query: { returnUrl: returnUrl.value }
    })
  } catch (error) {
    submitStatus.value = 'error'
    console.error("Registration error:", error)
  }
}

function handleOAuthSuccess(result) {
  // Handle successful OAuth login/registration
}

function handleOAuthError(error) {
  // Handle OAuth error
  console.error('OAuth error:', error)
}

const goToLogin = () => {
  router.push({
    name: 'login',
    query: { role: route.query.role || 'patient', returnUrl: returnUrl.value }
  })
}
</script>

<template>
  <div class="auth-page">
    <div class="auth-container">
      <NCard :title="dt('register.title')" class="auth-card">
        <div class="card-content">
          <div v-if="submitStatus === 'error'" class="mb-4">
            <NAlert type="error" :title="authError" />
          </div>

          <div v-if="submitStatus === 'success'" class="mb-4">
            <NAlert type="success" :title="successMessage" />
          </div>

          <NForm>
            <NFormItem :label="dt('register.fullNameLabel')" required>
              <NInput v-model:value="name" type="text" :placeholder="dt('register.fullNamePlaceholder')"
                :disabled="submitStatus === 'submitting'" />
            </NFormItem>

            <NFormItem :label="dt('register.emailLabel')" required>
              <NInput v-model:value="email" type="email" :placeholder="dt('register.emailPlaceholder')"
                :disabled="submitStatus === 'submitting'" />
            </NFormItem>

            <NFormItem :label="dt('register.languageLabel')" required>
              <NSelect v-model:value="language" :options="LANGUAGES" class="language-select"
                :placeholder="dt('register.languageLabel')" size="large" />
            </NFormItem>

            <NFormItem :label="dt('register.accountTypeLabel')" required>
              <NRadioGroup v-model:value="role">
                <NSpace>
                  <NRadio value="patient">
                    {{ dt('register.rolePatient') || 'Patient (find healthcare providers)' }}
                  </NRadio>
                  <NRadio value="provider">
                    {{ dt('register.roleProvider') || 'Provider (list your healthcare services)' }}
                  </NRadio>
                </NSpace>
              </NRadioGroup>
            </NFormItem>

            <div class="form-actions">
              <NButton type="primary" block :loading="submitStatus === 'submitting'" :disabled="!email || !name"
                @click="handleRegister">
                {{ dt('register.title') }}
              </NButton>
            </div>
          </NForm>

          <div class="auth-options">
            <NText depth="3">{{ dt('register.alreadyHaveAccountPrompt') }}</NText>
            <NButton text @click="goToLogin">{{ dt('login.title') }}</NButton>
          </div>
        </div>

        <div class="oauth-section">
          <div class="divider">
            <span>{{ dt('register.socialRegisterPrompt') }}</span>
          </div>

          <div class="oauth-buttons">
            <OAuthButton provider="google" :role="role" :language="language" @success="handleOAuthSuccess"
              @error="handleOAuthError" />
            <OAuthButton provider="github" :role="role" :language="language" @success="handleOAuthSuccess"
              @error="handleOAuthError" />
          </div>
        </div>
      </NCard>

      <div class="auth-info">
        <h2>{{ dt('register.welcomeHeader') }}</h2>
        <p>{{ dt('register.welcomeSubheader') }}</p>

        <ul>
          <li>{{ dt('register.benefits.languageSupport') }}</li>
          <li>{{ dt('register.benefits.contribute') }}</li>
          <li>{{ dt('register.benefits.favorites') }}</li>
          <li>{{ dt('register.benefits.reviews') }}</li>
          <li>{{ dt('register.benefits.quickAccess') }}</li>
        </ul>
      </div>
    </div>
  </div>
</template>


<style scoped>
.auth-page {
  min-height: calc(100vh - 200px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: #f8f9fa;
}

.auth-container {
  display: flex;
  max-width: 900px;
  width: 100%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
}

.auth-card {
  flex: 1;
  border-radius: 0;
  box-shadow: none;
}

.card-content {
  padding: 1rem 0;
}

.form-actions {
  margin-top: 0.5rem;
}

.language-select {
  max-width: 180px;
  text-align: left !important;
}

.auth-options {
  margin-top: 1rem;
  text-align: center;
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  align-items: center;
}

.auth-info {
  flex: 1;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 3rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.auth-info h2 {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.auth-info p {
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  opacity: 0.9;
}

.auth-info ul {
  list-style-type: none;
  padding: 0;
}

.auth-info li {
  margin-bottom: 0.8rem;
  display: flex;
  align-items: center;
  font-size: 1.1rem;
}

.auth-info li::before {
  content: "✓";
  margin-right: 0.5rem;
  font-weight: bold;
}

.mb-4 {
  margin-bottom: 1rem;
}

@media (max-width: 768px) {
  .auth-container {
    flex-direction: column-reverse;
  }

  .auth-info {
    padding: 2rem;
  }
}

.oauth-section {
  width: 100%;
}

.divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 12px 0;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid #e5e7eb;
}

.divider span {
  padding: 0 12px;
  color: #6b7280;
  font-size: 14px;
}
</style>